import { useState } from 'react';
import { Badge, Button, Checkbox, ControlRow, FormField, Input, Modal, ModalBody, ModalFooter, ModalHeader, Radio, useToast } from '@vd/ui';
import { X } from 'lucide-react';
import { readPattern, wholeSite, wholeSiteOrigin } from './captureProfilePattern';

export type CaptureMode = 'full' | 'metadata' | 'never';
export interface CaptureProfile {
  originPattern: string;
  mode: CaptureMode;
  extraDenyList: string[];
}

export interface CaptureProfileModalProps {
  open: boolean;
  onClose: () => void;
  initial?: Partial<CaptureProfile>;
  title?: string;
  /**
   * When provided, called with the form data instead of the internal fallback
   * (which just toasts "saved" as a UX placeholder). Preferred for new callers.
   */
  onSave?: (profile: CaptureProfile) => void;
}

/**
 * The profile editor. Mount it fresh for each profile (the caller keys it):
 * its fields are read from `initial` once.
 *
 * "Cover the whole site" is on for a new profile, and for an existing one whose
 * pattern is a whole site, so typing `https://app.example.com` covers the site.
 * It used to cover the home page alone - a bare address is an exact match - and
 * nothing on the form said so. Off, the form explains how to write a pattern and
 * says, as it is typed, what it will cover.
 */
export function CaptureProfileModal({ open, onClose, initial, title = 'New capture profile', onSave }: CaptureProfileModalProps) {
  const initialSite = initial?.originPattern ? wholeSiteOrigin(initial.originPattern) : null;
  const [coverSite, setCoverSite] = useState(!initial?.originPattern || initialSite !== null);
  const [address, setAddress] = useState(initialSite ?? initial?.originPattern ?? '');
  const [mode, setMode] = useState<CaptureMode>(initial?.mode ?? 'full');
  const [extras, setExtras] = useState<string[]>(initial?.extraDenyList ?? []);
  const [draft, setDraft] = useState('');
  const toast = useToast();

  const reading = coverSite ? wholeSite(address) : readPattern(address);
  const pattern = 'pattern' in reading ? reading.pattern : null;

  const toggleCoverSite = (next: boolean) => {
    // Unticked, the field shows the pattern the site became, to edit from.
    if (!next && pattern) setAddress(pattern);
    setCoverSite(next);
  };

  const addExtra = () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    // Case-insensitively, as the redactors compare: a stored `Session` is
    // already this name.
    if (!extras.some((existing) => existing.trim().toLowerCase() === v)) setExtras((e) => [...e, v]);
    setDraft('');
  };

  const save = () => {
    if (!pattern) return;
    if (onSave) {
      onSave({ originPattern: pattern, mode, extraDenyList: extras });
      return;
    }
    toast.push({
      variant: 'success',
      title: 'Profile saved',
      description: `${pattern} · ${mode === 'full' ? 'Full capture' : mode === 'metadata' ? 'Metadata only' : 'Never capture'}`,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} width={540}>
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>
        <ControlRow>
          <Checkbox checked={coverSite} onChange={(e) => toggleCoverSite(e.target.checked)} />
          <span style={{ fontSize: 14 }}>Cover the whole site</span>
        </ControlRow>

        <div style={{ height: 10 }} />

        <FormField
          label={coverSite ? 'Site' : 'Address pattern'}
          error={address.trim() && 'problem' in reading ? reading.problem : undefined}
        >
          <Input
            autoFocus
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={coverSite ? 'https://app.example.com' : 'https://app.example.com/admin/*'}
          />
        </FormField>

        {'covers' in reading && (
          <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5 }} aria-live="polite">
            <div style={{ color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text)' }}>Covers:</strong> {reading.covers}
            </div>
            {reading.note && <div style={{ color: 'var(--warning)', marginTop: 2 }}>{reading.note}</div>}
          </div>
        )}

        {!coverSite && <PatternHelp />}

        <div style={{ height: 14 }} />

        <FormField label="Capture mode">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ModeOption value="full" mode={mode} setMode={setMode}
              title="Full capture"
              desc="Screenshot, console, network (incl. bodies up to cap), errors, performance."
            />
            <ModeOption value="metadata" mode={mode} setMode={setMode}
              title="Metadata only"
              desc="Everything but request / response bodies. Safer for sensitive APIs."
            />
            <ModeOption value="never" mode={mode} setMode={setMode}
              title="Never capture"
              desc="The extension won't capture on pages this profile covers. Use for anything truly off-limits."
            />
          </div>
        </FormField>

        <div style={{ height: 14 }} />

        <FormField label="Extra header / query-param redactions" help="Each name is redacted as a request header and as a URL query parameter, on top of the defaults (Authorization, Cookie, api_key, etc.).">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {extras.map((x) => (
              <Badge key={x} variant="outline">
                {x}
                <button
                  onClick={() => setExtras((e) => e.filter((y) => y !== x))}
                  style={{ marginLeft: 4, padding: 0, color: 'var(--text-tertiary)', lineHeight: 0 }}
                  aria-label={`Remove ${x}`}
                >
                  <X size={10} />
                </button>
              </Badge>
            ))}
            {extras.length === 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>No extras - defaults apply.</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExtra(); } }}
              placeholder="header or param name, e.g. x-tenant-id"
            />
            <Button variant="secondary" onClick={addExtra}>Add</Button>
          </div>
        </FormField>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!pattern} onClick={save}>Save profile</Button>
      </ModalFooter>
    </Modal>
  );
}

/** How to write a pattern, shown while "Cover the whole site" is off. */
function PatternHelp() {
  const code = { fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--text)' } as const;
  const row = { display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: 10, alignItems: 'baseline' } as const;
  return (
    <div
      role="note"
      aria-label="How to write an address pattern"
      style={{
        marginTop: 10, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6,
        background: 'var(--bg-subtle)', fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      <div style={row}>
        <span style={{ ...code, overflowWrap: 'anywhere' }}>https://app.example.com/admin/*</span>
        <span>Every page under /admin, at any depth.</span>
      </div>
      <div style={row}>
        <span style={{ ...code, overflowWrap: 'anywhere' }}>https://app.example.com/admin</span>
        <span>That one address only.</span>
      </div>
      <div style={row}>
        <span style={{ ...code, overflowWrap: 'anywhere' }}>https://app.example.com/**</span>
        <span>Works too, and is saved as /*: one * already covers every level.</span>
      </div>
      <div style={{ marginTop: 4, fontWeight: 600, color: 'var(--text)' }}>Not allowed</div>
      <div style={row}>
        <span style={{ ...code, overflowWrap: 'anywhere' }}>https://*.example.com/*</span>
        <span>A * only works at the end. Add a profile for each subdomain.</span>
      </div>
      <div style={row}>
        <span style={{ ...code, overflowWrap: 'anywhere' }}>https://app.example.com/#/billing</span>
        <span>Nothing after #. Browsers never send it.</span>
      </div>
    </div>
  );
}

function ModeOption({ value, mode, setMode, title, desc }: {
  value: CaptureMode; mode: CaptureMode; setMode: (v: CaptureMode) => void;
  title: string; desc: string;
}) {
  const selected = mode === value;
  return (
    <label style={{
      display: 'flex', gap: 10, padding: 12,
      border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 8,
      background: selected ? 'var(--bg-subtle)' : 'var(--surface)',
      cursor: 'pointer',
    }}>
      <ControlRow>
        <Radio checked={selected} onChange={() => setMode(value)} />
      </ControlRow>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{desc}</div>
      </div>
    </label>
  );
}
